import React, { createContext, useContext, useEffect, useState } from "react";

const dict = {
  en: {
    nav: {
      services: "Services",
      portfolio: "Portfolio",
      process: "Process",
      about: "Why Us",
      contact: "Quote",
      cta: "Get a Quote",
    },
    hero: {
      eyebrow: "Sue Production — Creative Studio",
      title_1: "Content That",
      title_2: "Builds Influence.",
      subtitle:
        "We craft premium video content, personal brands, and AI-powered creative solutions for businesses ready to stand out.",
      cta_primary: "Get a Quote",
      cta_secondary: "View Our Work",
      scroll: "Scroll",
    },
    marquee: [
      "Videography",
      "Personal Branding",
      "AI Content",
      "Commercials",
      "Strategy",
      "Social Media",
      "Web Design",
      "Logo & Identity",
      "Production",
      "Direction",
    ],
    trust: {
      label: "By the numbers",
      title: "Crafted with intent. Measured by impact.",
      items: [
        { k: "Projects Completed", suffix: "+" },
        { k: "Brands Helped", suffix: "+" },
        { k: "Pieces of Content Produced", suffix: "+" },
        { k: "5-Star Reviews", suffix: "+" },
      ],
    },
    services: {
      label: "Services",
      title: "A studio built for ambitious brands.",
      subtitle:
        "Every engagement is designed around your audience, your story, and the long game.",
      items: [
        {
          title: "Professional Videography",
          desc: "Cinematic production with a director's eye. From single-camera interviews to multi-day commercial shoots.",
          benefits: ["Cinematic grade", "Pro-level crew", "Color & sound mastered"],
        },
        {
          title: "Personal Brand Development",
          desc: "We position founders, creators and executives as undeniable category leaders.",
          benefits: ["Narrative architecture", "Visual identity", "Content cadence"],
        },
        {
          title: "AI Content Creation",
          desc: "Next-generation creative powered by AI — at the speed of culture, with the polish of a studio.",
          benefits: ["AI video & imagery", "Scalable variants", "Rapid iteration"],
        },
        {
          title: "Commercial Advertising",
          desc: "Concept, script, shoot, edit. Performance creative engineered to convert.",
          benefits: ["Hook-first edits", "Platform-native", "Performance tested"],
        },
        {
          title: "Content Strategy",
          desc: "Where data meets taste. We design the content engine before pressing record.",
          benefits: ["Audience mapping", "Pillars & hooks", "Editorial calendar"],
        },
        {
          title: "Social Media Production",
          desc: "Always-on production pipelines that feed Instagram, TikTok, YouTube and LinkedIn.",
          benefits: ["Monthly drops", "Native formats", "Trend-aware"],
        },
        {
          title: "Web Design & Development",
          desc: "High-conversion websites and landing pages designed to match your visual identity — from concept to launch.",
          benefits: ["Design + build", "Mobile-first", "SEO ready"],
        },
        {
          title: "Logo & Brand Identity",
          desc: "Distinctive logos, typography systems and visual guidelines that make your brand impossible to confuse.",
          benefits: ["Logo suite", "Brand system", "Style guide"],
        },
      ],
    },
    portfolio: {
      label: "Selected work",
      title: "Cinematic. Strategic. Unmistakably yours.",
      subtitle: "A look at recent productions across commerce, lifestyle and personal brands.",
      categories: ["All", "Commercial", "Personal Brand", "Social", "AI"],
      open: "Open project",
      close: "Close",
    },
    why: {
      label: "Why Sue Production",
      title_1: "More Than Content.",
      title_2: "We Build Presence.",
      paragraph:
        "We don't shoot one-off videos and disappear. We become the creative partner that engineers your visual presence across every touchpoint — week after week, quarter after quarter.",
      pillars: [
        { t: "Strategic thinking", d: "Every frame ladders up to a business outcome." },
        { t: "Premium execution", d: "Cinema-grade gear, professional crews, post-production mastered." },
        { t: "Consistent branding", d: "A unified visual system across every platform." },
        { t: "High-quality production", d: "We refuse to publish anything we wouldn't put in a reel." },
        { t: "Long-term growth", d: "Compounding presence beats viral spikes. Every time." },
      ],
      commitment_label: "We come to you",
      commitment_title: "We travel to your location.",
      commitment_desc:
        "Based in Miami, Tulsa and Dallas — and on a plane the moment your project needs us on the ground. Wherever your story lives, we show up with the full crew.",
    },
    process: {
      label: "The Process",
      title: "From discovery to scale — in five disciplined steps.",
      steps: [
        { n: "01", t: "Discovery", d: "We listen. We audit. We map your brand, audience and ambition." },
        { n: "02", t: "Strategy", d: "Pillars, hooks, formats and KPIs. The blueprint before the camera." },
        { n: "03", t: "Production", d: "Cinematic shoot days with a full creative crew." },
        { n: "04", t: "Delivery", d: "Edits, color, sound and platform-native exports — on time." },
        { n: "05", t: "Scale & Optimize", d: "Measure, learn, iterate. The presence engine runs." },
      ],
    },
    testimonials: {
      label: "Clients",
      title: "Words from the brands we build with.",
      items: [
        {
          q: "Sue Production didn't just shoot content — they rebuilt how our brand shows up online. Eight months in, our inbound is unrecognizable.",
          n: "Marcus Reed",
          c: "Founder, Reed Capital",
        },
        {
          q: "The most professional studio I've worked with on either coast. They think like a creative director and execute like a network production.",
          n: "Sofía Marín",
          c: "CMO, Lunaire",
        },
        {
          q: "Our personal brand revenue tripled within four months. They run the entire content engine — I just show up on set.",
          n: "Daniel Cruz",
          c: "Real Estate Investor",
        },
        {
          q: "Cinematic, fast, and on-strategy. Sue Production raised the bar for what we expect from a creative partner.",
          n: "Aria Kapoor",
          c: "Director, Kapoor Studio",
        },
      ],
    },
    quote: {
      label: "Start a project",
      title: "Request Your Project Quote",
      subtitle:
        "Tell us about your brand and your ambition. We respond to qualified inquiries within 24 hours.",
      fields: {
        name: "Full Name",
        business: "Business Name",
        email: "Email",
        phone: "Phone Number",
        social: "Social Media Links",
        service: "Type of Service Needed",
        service_placeholder: "Select a service",
        budget: "Budget Range",
        budget_placeholder: "Select a budget range",
        custom_budget: "Custom Budget (USD)",
        description: "Project Description",
        description_placeholder: "Tell us about your goals, audience, and any references you love…",
        start: "Preferred Start Date",
        agree:
          "I understand projects require a minimum 2-month commitment.",
      },
      service_options: [
        "Professional Videography",
        "Personal Brand Development",
        "AI Content Creation",
        "Commercial Advertising",
        "Content Strategy",
        "Social Media Production",
        "Web Design & Development",
        "Logo & Brand Identity",
      ],
      budget_options: [
        "$2K – $5K",
        "$5K – $10K",
        "$10K – $25K",
        "$25K – $50K",
        "$50K – $100K",
        "$100K+",
      ],
      custom_budget_option: "Custom budget",
      custom_budget_placeholder: "Enter any amount in USD",
      custom_budget_error: "Enter a custom budget greater than $0 USD.",
      submit: "Request Consultation",
      submitting: "Sending…",
      success_title: "Request received.",
      success_desc: "We'll get back to you within 24 hours.",
      error: "Something went wrong. Please try again or email us directly.",
      validation_error: "Please review your information. The project description must contain at least 10 characters.",
      server_error: "We couldn't save your request right now. Please try again in a moment.",
      connection_error: "We couldn't connect to the quote service. Check your connection and try again.",
      email_directly: "Email us directly.",
      travel_highlight: "We travel to your location.",
      travel_desc: "Based in Miami, Tulsa and Dallas — on-site anywhere in the US and beyond.",
    },
    faq: {
      label: "FAQ",
      title: "Questions, answered.",
      items: [
        {
          q: "What services do you offer?",
          a: "Professional videography, personal brand development, AI content creation, commercial advertising, content strategy, and full social media production.",
        },
        {
          q: "Do you work with personal brands?",
          a: "Yes — we partner with founders, creators and executives who want a category-leading visual presence.",
        },
        {
          q: "Do you offer AI-generated content?",
          a: "Absolutely. AI is woven into our pipeline for imagery, video, ideation and rapid variant production — always with studio-grade polish.",
        },
        {
          q: "How long are contracts?",
          a: "Every engagement is scoped to your goals — from single productions to always-on content partnerships. We're flexible on terms once we know what you're building.",
        },
        {
          q: "What is included in content production?",
          a: "Pre-production strategy, shoot day(s) with a full crew, editing, color, sound, platform-native exports, and an asset library.",
        },
        {
          q: "How quickly can projects begin?",
          a: "Most engagements kick off within 7–14 days of signing. Rush starts are possible based on calendar availability.",
        },
        {
          q: "Do you travel to my location?",
          a: "Yes — we regularly fly to our clients across the US and internationally. Our home base is Miami, Tulsa and Dallas, but we bring the full crew wherever your story lives.",
        },
      ],
    },
    final_cta: {
      title_1: "Ready To Elevate",
      title_2: "Your Brand?",
      subtitle:
        "Book a consultation with our creative team. We'll map a path to category-leading presence.",
      cta: "Book a Consultation",
    },
    footer: {
      tagline: "Premium creative production for brands ready to stand out.",
      sections: { explore: "Explore", contact: "Contact", legal: "Legal" },
      privacy: "Privacy",
      terms: "Terms",
      rights: "All rights reserved.",
    },
    common: {
      back_to_top: "Back to top",
      language: "Language",
    },
  },
  es: {
    nav: {
      services: "Servicios",
      portfolio: "Portafolio",
      process: "Proceso",
      about: "Por qué",
      contact: "Cotización",
      cta: "Cotizar",
    },
    hero: {
      eyebrow: "Sue Production — Estudio Creativo",
      title_1: "Contenido Que",
      title_2: "Construye Influencia.",
      subtitle:
        "Creamos contenido de video premium, marcas personales y soluciones creativas con IA para empresas listas para destacar.",
      cta_primary: "Cotizar Proyecto",
      cta_secondary: "Ver Nuestro Trabajo",
      scroll: "Desplazar",
    },
    marquee: [
      "Videografía",
      "Marca Personal",
      "Contenido IA",
      "Comerciales",
      "Estrategia",
      "Redes Sociales",
      "Diseño Web",
      "Logo e Identidad",
      "Producción",
      "Dirección",
    ],
    trust: {
      label: "En cifras",
      title: "Hecho con intención. Medido por impacto.",
      items: [
        { k: "Proyectos Completados", suffix: "+" },
        { k: "Marcas Impulsadas", suffix: "+" },
        { k: "Piezas de Contenido", suffix: "+" },
        { k: "Reseñas 5 Estrellas", suffix: "+" },
      ],
    },
    services: {
      label: "Servicios",
      title: "Un estudio diseñado para marcas ambiciosas.",
      subtitle:
        "Cada proyecto se diseña en torno a tu audiencia, tu historia y el largo plazo.",
      items: [
        {
          title: "Videografía Profesional",
          desc: "Producción cinematográfica con ojo de director. Desde entrevistas a comerciales de varios días.",
          benefits: ["Calidad cinematográfica", "Equipo profesional", "Color y sonido mastered"],
        },
        {
          title: "Desarrollo de Marca Personal",
          desc: "Posicionamos fundadores, creadores y ejecutivos como referentes indiscutibles de su categoría.",
          benefits: ["Arquitectura narrativa", "Identidad visual", "Cadencia de contenido"],
        },
        {
          title: "Creación de Contenido IA",
          desc: "Creatividad de nueva generación con IA — al ritmo de la cultura, con acabado de estudio.",
          benefits: ["Video e imagen IA", "Variantes escalables", "Iteración rápida"],
        },
        {
          title: "Publicidad Comercial",
          desc: "Concepto, guion, rodaje, edición. Creatividad de performance diseñada para convertir.",
          benefits: ["Ediciones de gancho", "Nativo de plataforma", "Probado en performance"],
        },
        {
          title: "Estrategia de Contenido",
          desc: "Donde los datos se encuentran con el gusto. Diseñamos el motor antes de grabar.",
          benefits: ["Mapeo de audiencia", "Pilares y ganchos", "Calendario editorial"],
        },
        {
          title: "Contenido para Redes",
          desc: "Pipelines de producción always-on para Instagram, TikTok, YouTube y LinkedIn.",
          benefits: ["Entregas mensuales", "Formatos nativos", "Atento a tendencias"],
        },
        {
          title: "Diseño y Desarrollo Web",
          desc: "Sitios web y landing pages de alta conversión diseñados para tu identidad visual — del concepto al lanzamiento.",
          benefits: ["Diseño + desarrollo", "Mobile-first", "SEO listo"],
        },
        {
          title: "Logo e Identidad de Marca",
          desc: "Logos distintivos, sistemas tipográficos y guías visuales que hacen tu marca imposible de confundir.",
          benefits: ["Suite de logo", "Sistema de marca", "Manual de estilo"],
        },
      ],
    },
    portfolio: {
      label: "Trabajos seleccionados",
      title: "Cinematográfico. Estratégico. Inconfundiblemente tuyo.",
      subtitle: "Producciones recientes para comercio, lifestyle y marcas personales.",
      categories: ["Todos", "Comercial", "Marca Personal", "Social", "IA"],
      open: "Ver proyecto",
      close: "Cerrar",
    },
    why: {
      label: "Por qué Sue Production",
      title_1: "Más Que Contenido.",
      title_2: "Construimos Presencia.",
      paragraph:
        "No grabamos videos sueltos y desaparecemos. Nos convertimos en el socio creativo que diseña tu presencia visual en cada punto de contacto — semana tras semana, trimestre tras trimestre.",
      pillars: [
        { t: "Pensamiento estratégico", d: "Cada cuadro responde a un objetivo de negocio." },
        { t: "Ejecución premium", d: "Equipo de cine, crews profesionales, post-producción mastered." },
        { t: "Marca consistente", d: "Un sistema visual unificado en cada plataforma." },
        { t: "Producción de alta calidad", d: "Nada publicamos que no pondríamos en un reel." },
        { t: "Crecimiento a largo plazo", d: "La presencia que compounding gana al pico viral. Siempre." },
      ],
      commitment_label: "Vamos a ti",
      commitment_title: "Viajamos a tu ubicación.",
      commitment_desc:
        "Basados en Miami, Tulsa y Dallas — y en un avión cuando tu proyecto nos necesita en el terreno. Donde viva tu historia, llegamos con el equipo completo.",
    },
    process: {
      label: "El Proceso",
      title: "De descubrimiento a escala — en cinco pasos disciplinados.",
      steps: [
        { n: "01", t: "Descubrimiento", d: "Escuchamos. Auditamos. Mapeamos tu marca, audiencia y ambición." },
        { n: "02", t: "Estrategia", d: "Pilares, ganchos, formatos y KPIs. El plano antes de la cámara." },
        { n: "03", t: "Producción", d: "Días de rodaje cinematográfico con un crew creativo completo." },
        { n: "04", t: "Entrega", d: "Edición, color, sonido y exportaciones nativas — a tiempo." },
        { n: "05", t: "Escala y Optimización", d: "Medir, aprender, iterar. El motor de presencia funciona." },
      ],
    },
    testimonials: {
      label: "Clientes",
      title: "Palabras de las marcas con las que construimos.",
      items: [
        {
          q: "Sue Production no solo grabó contenido — redefinió cómo nuestra marca aparece online. A ocho meses, nuestro inbound es irreconocible.",
          n: "Marcus Reed",
          c: "Fundador, Reed Capital",
        },
        {
          q: "El estudio más profesional con el que he trabajado en cualquier costa. Piensan como dirección creativa y ejecutan como producción de TV.",
          n: "Sofía Marín",
          c: "CMO, Lunaire",
        },
        {
          q: "Los ingresos de mi marca personal se triplicaron en cuatro meses. Ellos manejan todo el motor de contenido — yo solo me presento al set.",
          n: "Daniel Cruz",
          c: "Inversionista Inmobiliario",
        },
        {
          q: "Cinematográfico, rápido y estratégico. Sue Production elevó el estándar de lo que esperamos de un socio creativo.",
          n: "Aria Kapoor",
          c: "Directora, Kapoor Studio",
        },
      ],
    },
    quote: {
      label: "Comenzar un proyecto",
      title: "Solicita Tu Cotización",
      subtitle:
        "Cuéntanos sobre tu marca y ambición. Respondemos a consultas calificadas en menos de 24 horas.",
      fields: {
        name: "Nombre Completo",
        business: "Nombre del Negocio",
        email: "Correo Electrónico",
        phone: "Teléfono",
        social: "Redes Sociales",
        service: "Servicio Deseado",
        service_placeholder: "Selecciona un servicio",
        budget: "Rango de Presupuesto",
        budget_placeholder: "Selecciona un rango",
        custom_budget: "Presupuesto Personalizado (USD)",
        description: "Descripción del Proyecto",
        description_placeholder: "Cuéntanos sobre tus objetivos, audiencia y referencias que te encanten…",
        start: "Fecha Preferida de Inicio",
        agree:
          "Entiendo que los proyectos requieren un compromiso mínimo de 2 meses.",
      },
      service_options: [
        "Videografía Profesional",
        "Desarrollo de Marca Personal",
        "Creación de Contenido IA",
        "Publicidad Comercial",
        "Estrategia de Contenido",
        "Contenido para Redes",
        "Diseño y Desarrollo Web",
        "Logo e Identidad de Marca",
      ],
      budget_options: [
        "$2K – $5K",
        "$5K – $10K",
        "$10K – $25K",
        "$25K – $50K",
        "$50K – $100K",
        "$100K+",
      ],
      custom_budget_option: "Presupuesto personalizado",
      custom_budget_placeholder: "Ingresa cualquier cantidad en USD",
      custom_budget_error: "Ingresa un presupuesto personalizado mayor a $0 USD.",
      submit: "Solicitar Consulta",
      submitting: "Enviando…",
      success_title: "Solicitud recibida.",
      success_desc: "Te responderemos en menos de 24 horas.",
      error: "Algo salió mal. Inténtalo de nuevo o escríbenos.",
      validation_error: "Revisa la información. La descripción del proyecto debe tener al menos 10 caracteres.",
      server_error: "No pudimos guardar tu solicitud en este momento. Inténtalo nuevamente en unos segundos.",
      connection_error: "No pudimos conectar con el servicio de cotizaciones. Revisa tu conexión e inténtalo de nuevo.",
      email_directly: "Escríbenos directamente.",
      travel_highlight: "Viajamos a tu ubicación.",
      travel_desc: "Basados en Miami, Tulsa y Dallas — en el terreno donde nos necesites en EE.UU. y el mundo.",
    },
    faq: {
      label: "FAQ",
      title: "Preguntas, resueltas.",
      items: [
        {
          q: "¿Qué servicios ofrecen?",
          a: "Videografía profesional, desarrollo de marca personal, creación de contenido con IA, publicidad comercial, estrategia de contenido y producción completa para redes.",
        },
        {
          q: "¿Trabajan con marcas personales?",
          a: "Sí — trabajamos con fundadores, creadores y ejecutivos que buscan una presencia visual líder en su categoría.",
        },
        {
          q: "¿Ofrecen contenido generado por IA?",
          a: "Absolutamente. La IA está integrada en nuestro pipeline para imagen, video, ideación y producción rápida de variantes — siempre con acabado de estudio.",
        },
        {
          q: "¿Cuánto duran los contratos?",
          a: "Cada proyecto se ajusta a tus objetivos — desde producciones únicas hasta partnerships de contenido always-on. Somos flexibles con los términos una vez sabemos qué estás construyendo.",
        },
        {
          q: "¿Qué incluye la producción de contenido?",
          a: "Estrategia de pre-producción, día(s) de rodaje con crew completo, edición, color, sonido, exportaciones nativas y librería de assets.",
        },
        {
          q: "¿Qué tan rápido pueden iniciar los proyectos?",
          a: "La mayoría arranca en 7–14 días después de firmar. Inicios urgentes son posibles según calendario.",
        },
        {
          q: "¿Viajan a mi ubicación?",
          a: "Sí — viajamos regularmente para nuestros clientes en Estados Unidos e internacionalmente. Nuestra base es Miami, Tulsa y Dallas, pero llegamos con el equipo completo donde viva tu historia.",
        },
      ],
    },
    final_cta: {
      title_1: "¿Listo Para Elevar",
      title_2: "Tu Marca?",
      subtitle:
        "Agenda una consulta con nuestro equipo creativo. Trazaremos el camino hacia una presencia líder.",
      cta: "Agendar Consulta",
    },
    footer: {
      tagline: "Producción creativa premium para marcas listas para destacar.",
      sections: { explore: "Explorar", contact: "Contacto", legal: "Legal" },
      privacy: "Privacidad",
      terms: "Términos",
      rights: "Todos los derechos reservados.",
    },
    common: {
      back_to_top: "Volver arriba",
      language: "Idioma",
    },
  },
};

const I18nContext = createContext({
  lang: "en",
  t: dict.en,
  setLang: () => {},
});

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    return localStorage.getItem("sue.lang") || "en";
  });

  useEffect(() => {
    localStorage.setItem("sue.lang", lang);
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  const setLang = (l) => setLangState(l);
  const t = dict[lang] || dict.en;

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);
